import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Check as CheckIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';

import { ISubscriptionPlan } from '../../../../store/V2/SubscriptionPlansSlice';
import { getFormatedDurationByMinutes } from '../../../../helper';

interface SubscriptionPlanCardProps {
  plan: ISubscriptionPlan;
  selected: boolean;
  onClick: () => void;
}

const containerStyles: SxProps = {
  width: 'calc(100% - 30px)',
  maxWidth: '410px',
  borderRadius: '20px',
};

const cardActionAreaStyles: SxProps = {
  '.MuiCardActionArea-focusHighlight': {
    background: 'transparent',
  },
};

const mostPopularChipStyles: SxProps = {
  backgroundColor: '#1E293B',
  color: '#F8FAFC',
  borderRadius: '6px',
  fontWeight: 600,
  fontSize: '10px',
  padding: '6px 10px',
  height: 'unset',
  '& .MuiChip-label': {
    pl: 0,
    pr: 0,
  },
};

const SubscriptionPlanCard: FunctionComponent<SubscriptionPlanCardProps> = ({
  plan,
  selected,
  onClick,
}) => {
  // State
  const [maxHeight, setMaxHeight] = useState(0);

  const border = selected ? '2px solid #01B8AF' : '2px solid transparent';

  useEffect(() => {
    const features = document.querySelectorAll(
      '.subscription-plan-features-container'
    );

    let max = 0;

    features.forEach((feature) => {
      max = Math.max(max, feature.clientHeight);
    });

    setMaxHeight(max);
  }, []);

  return (
    <Card elevation={0} sx={{ ...containerStyles, border }}>
      <CardActionArea sx={cardActionAreaStyles} onClick={onClick}>
        <CardContent style={{ padding: '30px' }}>
          <div style={{ marginBottom: '15px' }}>
            <Typography
              fontSize="18px"
              fontWeight={600}
              color="#01A79F"
              className="ellipsis-text one-line"
            >
              {plan.name}
            </Typography>
          </div>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb="32px"
            minHeight="30px"
          >
            <Stack direction="row" alignItems="center" gap="20px">
              <Typography fontSize="18px" fontWeight={600} color="#01A79F">
                {plan.creditsChecked && plan.credits
                  ? getFormatedDurationByMinutes({
                      minutes: plan.credits || 0,
                      format: 'type2',
                    })
                  : ' '}
              </Typography>
              {plan.isMostPopular ? (
                <Chip sx={mostPopularChipStyles} label="Most Popular 🔥" />
              ) : null}
            </Stack>
            {selected ? (
              <CheckCircleIcon
                sx={{ color: '#01A79F', width: '20px', height: '20px' }}
              />
            ) : (
              <CircleIcon
                sx={{ color: '#E2E8F0', width: '20px', height: '20px' }}
              />
            )}
          </Stack>
          <Stack mb="32px">
            <Typography fontSize="18px" fontWeight={400} color="#647488">
              <span
                style={{ fontSize: '40px', fontWeight: 700, color: '#334155' }}
              >
                {plan.currency} {plan.amount || 0}
              </span>
              &nbsp;/ per {plan.billingPeriod.type.toLowerCase().slice(0, -1)}
            </Typography>
          </Stack>
          <Stack
            gap="12px"
            className="subscription-plan-features-container"
            sx={{ minHeight: maxHeight }}
          >
            {plan.features.map((item, index) => (
              <Stack
                key={`subscription-plan-feature-${index}`}
                direction="row"
                alignItems="center"
              >
                <CheckIcon
                  style={{
                    marginRight: '10px',
                    color: '#1E293B',
                    width: '18px',
                    height: '18px',
                  }}
                />
                <Typography color="#647488" fontWeight="400" fontSize="18px">
                  {item}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default SubscriptionPlanCard;
